import { getPercentageInfo, getShowCount } from '@/pages/opinion/utils';
import {
  geoCoordMapEn,
  geoCoordMapCn,
  geoCoordRegionEn,
  geoCoordRegionCn,
  regionTarget,
} from './data';
import { regionData, regionType, region } from './type';
import { formatter } from '../ChartCommon/formatter';
import { getLocale } from 'umi';
import {
  countryCn,
  countryEnMirror,
  regionCn,
  regionEnMirror,
} from '../../constants';

export const worldMapConfig = ({
  formatMessage,
  data,
  regionType = 'region',
  mentionsMax,
  mentionsTotal,
  keyMentions = 'Mentions',
}: {
  data: regionData;
  unidentifiedMentions: number;
  regionType: regionType;
  mentionsMax: number;
  mentionsTotal: number;
  formatMessage: any;
  getLocale: any;
  keyMentions?: string;
}) => {
  const convertData = function (data: region[]) {
    let res = [];
    if (Array.isArray(data) && data?.length) {
      for (let index = 0; index < data.length; index++) {
        const { name, value } = data[index];
        let replaceBlankName = name.replace(/\s+/, '');

        let geoCoord =
          regionType === 'market'
            ? geoCoordMapEn[name] || geoCoordMapEn[replaceBlankName]
            : geoCoordRegionEn[name] || geoCoordRegionEn[replaceBlankName];
        if (getLocale() === 'zh-CN') {
          geoCoord =
            regionType === 'market'
              ? geoCoordMapCn[name] || geoCoordMapCn[replaceBlankName]
              : geoCoordRegionCn[name] || geoCoordRegionCn[replaceBlankName];
        }

        if (geoCoord && value) {
          res.push({
            name,
            value: geoCoord.concat(value),
          });
        } else {
          console.log('symbolSize not find', name, value);
        }
      }
    }
    return res;
  };

  const mapFormatter = (params: any) => {
    let type = params?.seriesName;

    const res = data.find((item) => item.sentimentType === type)?.data;
    if (!res) return;
    return formatter({
      title: params.name,
      tables: [
        {
          showHeader: false,
          records: [
            {
              type: formatMessage({ id: keyMentions }),
              score: `${getShowCount(params?.value[2])} (${getPercentageInfo(
                params?.value[2],
                mentionsTotal,
                2,
              )})`,
            },
            {
              type: formatMessage({ id: 'Sentiment' }),
              score: `${res[params.dataIndex].avg_sentiment?.toFixed(2)}`,
            },
          ],
          columns: [
            {
              key: 'type',
            },
            {
              weight: 'bold',
              key: 'score',
              align: 'right',
            },
          ],
        },
      ],
    });
  };

  return {
    tooltip: {
      padding: 0,
      trigger: 'item',
      formatter: (params: any) => mapFormatter(params),
      borderWidth: 0,
    },
    legend: {
      type: 'scroll',
      itemHeight: 8,
      itemWidth: 8,
      icon: 'circle',
      bottom: -2,
      itemGap: 18,
      pageIconColor: 'rgb(47, 69, 84)', // 翻页下一页的三角按钮颜色
      pageIconInactiveColor: 'rgb(170, 170, 170)', // 翻页（即翻页到头时）
      pageIconSize: 12, // 翻页按钮大小
      pageButtonPosition: 'end', // start
      textStyle: {
        // 图例文字的样式
        color: '#888888',
        fontSize: 12,
        lineHeight: 16,
      },
    },
    backgroundColor: '#ededed',
    geo: {
      label: {
        show: true, // 显示地区名称
        formatter: (params: any) => {
          let result = '';
          if (regionType === 'region') {
            result = regionTarget?.[params?.name] ?? '';
          } else if (regionType === 'market') {
            if (params?.name === 'Russia') result = params?.name;
            if (params?.name === 'Poland') result = '';
            result = Object.keys(regionTarget).includes(params?.name)
              ? params?.name
              : '';
          }

          if (result && getLocale() === 'zh-CN') {
            return (
              regionCn[regionEnMirror[result]] ||
              countryCn[countryEnMirror[result]] ||
              result
            );
          }

          return result;
        },
        position: [1, 100], // 相对的百分比
        fontSize: 10,
        color: '#a6a6a6',
        offset: [2, 0],
        align: 'center',
      },
      itemStyle: {
        areaColor: '#f7f7f7', // 地形图区域颜色
        borderColor: '#ffffff',
      },
      show: true,
      map: 'world',
      roam: true,
      zoom: 1.2, // 当前视角的缩放比例
      scaleLimit: {
        max: 5,
        min: 1, // 设置默认缩放效果
      },
      top: '10%', // 距离顶部距离
      bottom: '20%',
      // layoutCenter: ['30%', '30%'],
      //如果宽高比大于 1 则宽度为 100，如果小于 1 则高度为 100，保证了不超过 100x100 的区域
      // layoutSize: 100,
      // nameMap: NAMES,  // 名称映射 （NAMES为中文）
      silent: true, // true为不触发鼠标事件
    },

    series: data?.map((dataItem) => {
      return {
        name: dataItem?.sentimentType,
        type: 'scatter',
        coordinateSystem: 'geo',
        legendHoverLink: false, // 是否启用图例 hover 时的联动高亮。
        symbolSize: function (val: number[]) {
          const baseMax = 60;
          const baseMin = 7;
          const newVal = mentionsMax && (val[2] / mentionsMax) * baseMax;

          // 这里是已最大mentions为基准 判断是否达到70（最大值） 超过70 按70显示，低于70且大于7 则不变 低于7按7显示（最小值）

          // console.log(
          //   'init value',
          //   val[2],
          //   'symbolSize',
          //   newVal,
          //   'max',
          //   mentionsMax,
          // );

          return newVal >= baseMax
            ? baseMax
            : newVal < baseMax && newVal > baseMin
            ? newVal
            : baseMin;
        },
        itemStyle: {
          color: dataItem?.color,
          opacity: 0.8,
        },
        data: convertData(dataItem?.data || []),
        encode: {
          value: 2,
        },
        label: {
          formatter: '',
          position: 'right',
          show: false,
        },
        emphasis: {
          itemStyle: {
            color: dataItem?.color,
            opacity: 0.8,
            borderWidth: 2,
            borderColor: '#000000',
          },
          label: {
            show: true,
          },
        },
      };
    }),
  };
};
